import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingDown, AlertTriangle } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const LossCalculator = () => {
  const [searches, setSearches] = useState([1500]);
  const [avgValue, setAvgValue] = useState([350]);

  const monthlyLoss = useMemo(() => {
    // assume page-1 captures ~32% CTR; you're getting 0 if not ranking
    // realistic capture if ranked: searches * 0.32 * 0.04 close rate
    const leads = Math.round(searches[0] * 0.32 * 0.04);
    return leads * avgValue[0];
  }, [searches, avgValue]);

  return (
    <section className="py-20 md:py-28 bg-muted/40">
      <div className="page-container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/10 text-destructive text-xs font-semibold uppercase tracking-wider mb-4">
            <AlertTriangle className="w-3.5 h-3.5" /> The hidden cost of page two
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground">
            How much money are you{" "}
            <span className="text-destructive">leaving on the table?</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Drag the sliders. The math doesn't lie.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto bg-card rounded-3xl p-8 md:p-10 shadow-xl border border-border"
        >
          <div className="space-y-8">
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="font-semibold text-foreground">
                  Monthly searches in Culver City for what you sell
                </label>
                <span className="font-heading text-2xl font-bold text-secondary">
                  {searches[0].toLocaleString()}
                </span>
              </div>
              <Slider
                value={searches}
                onValueChange={setSearches}
                min={100}
                max={10000}
                step={100}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-3">
                <label className="font-semibold text-foreground">
                  Average customer is worth
                </label>
                <span className="font-heading text-2xl font-bold text-secondary">
                  ${avgValue[0].toLocaleString()}
                </span>
              </div>
              <Slider
                value={avgValue}
                onValueChange={setAvgValue}
                min={50}
                max={5000}
                step={50}
              />
            </div>

            <div className="pt-6 border-t border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-destructive" />
                </div>
                <div className="text-sm text-muted-foreground uppercase tracking-wider font-semibold">
                  You're losing approximately
                </div>
              </div>
              <div className="font-heading text-5xl md:text-7xl font-bold text-destructive">
                ${monthlyLoss.toLocaleString()}
                <span className="text-2xl md:text-3xl text-muted-foreground font-medium">
                  {" "}
                  / month
                </span>
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                That's{" "}
                <span className="font-bold text-foreground">
                  ${(monthlyLoss * 12).toLocaleString()} a year
                </span>{" "}
                walking into your competitor's door.
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LossCalculator;
